import React from 'react';
import Icon from '../../../components/AppIcon';

import useSupabase from '../../../hooks/useSupabase';

const OrderCard = ({ 
  order, 
  onStatusChange, 
  onViewDetails, 
  showQuickActions = true 
}) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'ASSIGNED':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'IN_PROGRESS':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'DONE':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'HIGH':
        return 'text-red-600';
      case 'MEDIUM':
        return 'text-amber-600';
      case 'LOW':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };

  const getPriorityBadge = (priority) => {
    const colorClass = getPriorityColor(priority);
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colorClass} bg-opacity-10`}>
        {priority}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const colorClass = getStatusColor(status);
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded border ${colorClass}`}>
        {status?.replace('_', ' ')}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      const { error } = await orders?.update(order?.id, { 
        status: newStatus,
        ...(newStatus === 'completed' && { actual_completion: new Date()?.toISOString() })
      });
      
      if (error) {
        console.error('Error updating order status:', error);
        return;
      }

      onStatusChange?.(order?.id, newStatus);
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const { orders } = useSupabase();

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-3">
      {/* Order Header */}
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="font-semibold text-gray-900 text-lg">
            {order?.order_number}
          </h3>
          <p className="text-sm text-gray-600">{order?.title}</p>
        </div>
        <div className="flex items-center space-x-2">
          {getPriorityBadge(order?.priority)}
          {getStatusBadge(order?.status)}
        </div>
      </div>

      {/* Vehicle Info */}
      <div className="flex items-center text-sm text-gray-600 mb-3">
        <Icon name="Car" size={16} className="text-primary" />
        <span>
          {order?.vehicle?.year} {order?.vehicle?.make} {order?.vehicle?.model}
          {order?.vehicle?.vin && (
            <span className="ml-2 text-xs bg-gray-100 px-2 py-1 rounded">
              VIN: {order?.vehicle?.vin?.slice(-6)}
            </span>
          )}
        </span>
      </div>

      {/* Customer Info */}
      <div className="flex items-center text-sm text-gray-600 mb-3">
        <Icon name="User" size={14} className="text-text-secondary" />
        <span>{order?.customer_name}</span>
        {order?.customer_phone && (
          <a
            href={`tel:${order?.customer_phone}`}
            className="ml-2 text-blue-500 hover:text-blue-600"
          >
            <Icon name="Phone" size={14} className="mr-1" />
          </a>
        )}
      </div>

      {/* Service Items */}
      {order?.order_service_items?.length > 0 && (
        <div className="mb-3">
          <p className="text-xs text-gray-500 mb-1">Services:</p>
          <div className="space-y-1">
            {order?.order_service_items?.slice(0, 2)?.map((item) => (
              <div key={item?.id} className="text-sm text-gray-700">
                • {item?.service_item?.name}
                {item?.estimated_hours && (
                  <span className="text-gray-500 ml-1">
                    ({item?.estimated_hours}h)
                  </span>
                )}
              </div>
            ))}
            {order?.order_service_items?.length > 2 && (
              <p className="text-xs text-gray-500">
                +{order?.order_service_items?.length - 2} more services
              </p>
            )}
          </div>
        </div>
      )}

      {/* Timing */}
      <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
        {order?.appointment_date && (
          <div className="flex items-center">
            <Icon name="Clock" size={14} className="text-text-secondary" />
            <span>{new Date(order?.appointment_date)?.toLocaleDateString()}</span>
          </div>
        )}
        {order?.estimated_completion && (
          <div className="flex items-center">
            <Icon name="Calendar" size={14} className="text-text-secondary" />
            <span>Due: {new Date(order?.estimated_completion)?.toLocaleDateString()}</span>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      {showQuickActions && (
        <div className="flex space-x-2">
          <button
            onClick={() => onViewDetails?.(order?.id)}
            className="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-200 transition-colors"
          >
            View Details
          </button>
          
          {order?.status === 'assigned' && (
            <button
              onClick={() => handleStatusUpdate('in_progress')}
              className="flex-1 px-3 py-2 bg-blue-500 text-white rounded-md text-sm font-medium hover:bg-blue-600 transition-colors"
            >
              Start Work
            </button>
          )}
          
          {order?.status === 'in_progress' && (
            <button
              onClick={() => handleStatusUpdate('completed')}
              className="flex-1 px-3 py-2 bg-green-500 text-white rounded-md text-sm font-medium hover:bg-green-600 transition-colors"
            >
              Complete
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default OrderCard;